// frontend/src/features/livestock/useAddPhotosPicker.ts
import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useTank } from '@/contexts/TankContext';
import type { InboxLocationState } from './InboxPhotoGrid';
import { photoUploadQueue, type UploadAssignTo } from './photoUploadQueue';
import { useOpenInTank } from './useOpenInTank';

export interface AddPhotosPicker {
  /** FileButton's onChange: queues the picked photos for the selected tank. */
  onPick: (files: File[]) => void;
  /** FileButton's resetRef, so picking the same photos again still fires a change. */
  resetRef: RefObject<() => void>;
}

/**
 * Photos go to the selected tank's inbox through the upload queue (plan 4.1).
 * One photo that uploaded, or was already in the inbox waiting to be sorted,
 * opens its assign view; one in the inbox in another state (still being
 * processed, or assigned right now) opens the inbox, of the tank it was picked
 * for (selected again if the user switched meanwhile). Not if the user has
 * moved on from the page while it uploaded. Picked on the inbox grid
 * (fromInbox), the photo's Back returns to the grid.
 *
 * With assignTo 'tank' (the Tank Shots page) every photo is added to Tank
 * Shots once it is in the inbox, and nothing is opened: the strip says how it
 * went.
 */
export function useAddPhotosPicker({
  fromInbox = false,
  assignTo,
}: { fromInbox?: boolean; assignTo?: UploadAssignTo } = {}): AddPhotosPicker {
  const { selectedTank } = useTank();
  const openInTank = useOpenInTank();
  const resetRef = useRef<() => void>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
    };
  }, []);

  const onPick = useCallback(
    (files: File[]): void => {
      resetRef.current?.();
      if (!selectedTank) return;
      const tankId = selectedTank.id;
      const batch = photoUploadQueue.enqueue({
        tankId,
        tankName: selectedTank.name,
        files,
        ...(assignTo && { assignTo }),
      });
      if (!batch || files.length !== 1 || assignTo) return;
      void batch.done.then((result): void => {
        const outcome = result.files[0]?.outcome;
        if (!mounted.current || !outcome) return;
        const pending =
          (outcome.kind === 'uploaded' && !outcome.processing) ||
          (outcome.kind === 'in_inbox' && outcome.status === 'pending');
        if (pending && outcome.id !== null) {
          photoUploadQueue.dismiss(batch.batchId);
          const path = `/livestock/photos/${outcome.id}`;
          const state: InboxLocationState = { fromInbox: true };
          void (fromInbox ? openInTank(tankId, path, { state }) : openInTank(tankId, path));
        } else if (outcome.kind === 'in_inbox' || outcome.kind === 'uploaded') {
          photoUploadQueue.dismiss(batch.batchId);
          void openInTank(tankId, '/livestock/photos');
        }
      });
    },
    [selectedTank, openInTank, fromInbox, assignTo]
  );

  return { onPick, resetRef };
}
